import { AsyncThunk, createDraftSafeSelector, createEntityAdapter, createSlice, Draft, EntityId, PayloadAction, Reducer, SliceCaseReducers, ValidateSliceCaseReducers } from "@reduxjs/toolkit";
import { AsyncEntityState, AsyncEntityThunkConfig, createAsyncEntityInitialState } from "../Thunks/asyncEntityStateUtil";


export const createAsyncEntitySlice = <T, Id extends EntityId, Reducers extends (SliceCaseReducers<AsyncEntityState<T, Id>>), RejectedValue>(
  {name = "", reducers, fetchByIdsThunk, selectId }: {name: string, reducers: ValidateSliceCaseReducers<AsyncEntityState<T, Id>, Reducers>, fetchByIdsThunk: AsyncThunk<T[], Draft<Id>[], AsyncEntityThunkConfig<RejectedValue>>, selectId: (entity: T) => Id}
) => {

  const entityAdapter = createEntityAdapter({selectId});

  const {updateMany, updateOne, upsertOne, upsertMany, addOne, addMany, removeAll, removeOne} = entityAdapter;
  
  const initialState = createAsyncEntityInitialState(entityAdapter.getInitialState());

  const slice = createSlice({
    name,
    initialState,
    reducers: {
      // default actions -- can be overwritten in the supplied reducers if needed.
      updateMany,
      updateOne,
      upsertOne,
      upsertMany,
      addOne,
      addMany,
      removeAll,
      removeOne,
      // default action
      clearPendingIds: (state: Draft<AsyncEntityState<T, Id>>) => {state.enqueueIds = []},
      addPendingId: (state: Draft<AsyncEntityState<T, Id>>, action: PayloadAction<Draft<Id>>) => {
        state.enqueueIds.push(action.payload)
      },
      addLoadingIds: (state: Draft<AsyncEntityState<T, Id>>, action: PayloadAction<Draft<Id[]>>) => {
        state.loadingIds.push(...action.payload)
      },
      ...reducers      
    },
    extraReducers(builder) {
      builder.addCase(fetchByIdsThunk.pending, (state, action) => {
        state.status = "loading";
      })
      builder.addCase(fetchByIdsThunk.fulfilled, (state, action) => {
        state.status = "succeeded";
        entityAdapter.upsertMany(state, action.payload);
        const now = Date.now();
        
        for (const id of action.meta.arg) {
          (state.lastFetchedById as Record<Id, number>)[id as Id] = now;
        }
        state.loadingIds = state.loadingIds.filter((id) => !action.meta.arg.includes(id));
        
      })
      builder.addCase(fetchByIdsThunk.rejected, (state, action) => {
        state.status = "failed"
        state.loadingIds = state.loadingIds.filter((id) => !action.meta.arg.includes(id))
      })
    },
    selectors: {
      selectLoadingIds: createDraftSafeSelector(
      (state: AsyncEntityState<T, Id>) => state.loadingIds,
      (_, id: Id) => id,
      (loadingIds: Id[], id: Id) => loadingIds.includes(id)
    )
    } 
  })

  return {entityAdapter, reducer: slice.reducer as Reducer<typeof initialState>, actions: slice.actions, getSelectors: slice.getSelectors}
}